import { useEffect, useState } from "react";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Card from "../components/Card";
import GroupStatusIndicator, {
  type StepState,
} from "../components/GroupStatusIndicator";
import ShipmentStatusIndicator, {
  getCurrentShipmentStep,
  getShipmentSteps,
} from "../components/ShipmentStatusIndicator";
import { type CarrierShipmentStatus } from "../services/shipping.service";
import {
  ArrowRight,
  BadgeCheck,
  CalendarClock,
  CircleCheck,
  Clock,
  CreditCard,
  Download,
  Eye,
  Factory,
  FileSpreadsheet,
  MapPin,
  Palette,
  Pencil,
  TrendingUp,
  Truck,
  type LucideIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import OrderDetailsModal from "../components/OrderDetailsModal";
import PreviewModal from "../components/PreviewModal";
import {
  addWorkingDays,
  calendarDaysUntil,
  downloadFile,
  filenameFromUrl,
  formatDeliveryWindow,
  isValidHttpUrl,
} from "../lib/utils";
import { pickGroupDeliveryAddress } from "../components/DeliveryAddressForm";
import toast from "react-hot-toast";
import ChangeDeliveryAddressModal from "../components/ChangeDeliveryAddressModal";
import OrderSummaryModal from "../components/OrderSummaryModal";
import { productService } from "../services/product.service";

function getRecipientField(
  address: Record<string, unknown> | null | undefined,
  field: string,
) {
  if (!address) return "";
  const value = address[`recipient_${field}`] ?? address[`recepient_${field}`];
  return typeof value === "string" ? value.trim() : "";
}

type GroupStatusStep = {
  id: number;
  label: string;
  shortLabel?: string;
  icon: LucideIcon;
  completeOnArrive?: boolean;
  inProgressStepId?: number;
};

const GROUP_STATUS_STEPS: GroupStatusStep[] = [
  { id: 2, label: "Pago pendiente", icon: CreditCard },
  { id: 3, label: "Order confirmado", icon: FileSpreadsheet },
  { id: 5, label: "Diseño solicitado", icon: Palette },
  {
    id: 6,
    label: "Diseño pendiente de confirmación por parte del organizador.",
    shortLabel: "Por confirmar",
    icon: BadgeCheck,
    completeOnArrive: true,
  },
  { id: 7, label: "Cambios solicitados", icon: Pencil, inProgressStepId: 8 },
  {
    id: 8,
    label: "Diseño confirmado",
    icon: BadgeCheck,
    completeOnArrive: true,
  },
];

function isStepCompleted(step: GroupStatusStep, statusId: number) {
  if (step.completeOnArrive || step.inProgressStepId != null) {
    return statusId >= step.id;
  }

  return statusId > step.id;
}

function getStepStates(statusId: number): StepState[] {
  const steps = GROUP_STATUS_STEPS;
  const lastId = steps[steps.length - 1].id;

  if (statusId > lastId) {
    return steps.map(() => "completed");
  }

  const lastCompletedIndex = steps.reduce(
    (lastIndex, step, index) =>
      isStepCompleted(step, statusId) ? index : lastIndex,
    -1,
  );

  return steps.map((_, index) => {
    if (index <= lastCompletedIndex) return "completed";
    if (index === lastCompletedIndex + 1) return "progress";
    return "upcoming";
  });
}

function getCurrentStepNumber(states: StepState[]) {
  const progressIndex = states.findIndex((state) => state === "progress");
  if (progressIndex >= 0) return progressIndex + 1;

  const lastCompletedIndex = states.lastIndexOf("completed");
  if (lastCompletedIndex >= 0) return lastCompletedIndex + 1;

  return 1;
}

export default function OrderDetailsPage() {
  const [userData, setUserData] = useState<any>(null);
  const [shippingStatus] = useState<CarrierShipmentStatus | null>(null);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [isChangeAddressOpen, setIsChangeAddressOpen] = useState(false);
  const [preview, setPreview] = useState<{
    url: string;
    title: string;
    kind?: "unsupported";
    filename: string;
  } | null>(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [priceResult, setPriceResult] = useState<any>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [deliveryChoice, setDeliveryChoice] = useState<'individual' | 'grouped'>('individual');

  const navigate = useNavigate();

  useEffect(() => {
    const authUserData = localStorage.getItem("userData");
    if (authUserData) {
      setUserData(JSON.parse(authUserData));
    } else {
      toast.error("No tienes acceso a este grupo");
      navigate("/");
    }
  }, []);

  const statusId =
    userData?.group?.status?.id ?? userData?.group?.status_id ?? 5;
  const stepStates = getStepStates(statusId);
  const currentStepNumber = getCurrentStepNumber(stepStates);
  const timelineSteps = GROUP_STATUS_STEPS.map((step, index) => ({
    ...step,
    state: stepStates[index],
  }));
  const currentStatusLabel =
    timelineSteps.find((step) => step.state === "progress")?.label ??
    [...timelineSteps].reverse().find((step) => step.state === "completed")
      ?.label ??
    GROUP_STATUS_STEPS[0].label;

  const rawFinalArtsUrl =
    userData?.group?.final_arts_url ||
    userData?.group?.design?.final_arts_url ||
    null;
  const finalArtsUrl = isValidHttpUrl(rawFinalArtsUrl) ? rawFinalArtsUrl.trim() : null;
  const groupStatusId =
    userData?.group?.status?.id ?? userData?.group?.status_id;
  const orderConfirmed = Boolean(
    userData?.group?.order_confirmed ??
    (groupStatusId != null && groupStatusId >= 8),
  );
  const inProduction = Boolean(userData?.group?.in_production);
  const shipmentSteps = getShipmentSteps({
    orderConfirmed,
    inProduction,
    shippingStatus,
  });
  const currentShippingStep = getCurrentShipmentStep(shipmentSteps);
  const ShippingBadgeIcon = currentShippingStep?.icon ?? Factory;

  const product = userData?.user?.product ?? userData?.product;
  const customizations =
    userData?.user?.customizations ?? userData?.customizations ?? [];
  const shippingDateIso = userData?.group?.shipping_date as string | undefined;
  const isPendingDelivery = groupStatusId === 2 || !shippingDateIso;
  const maxDeliveryDate = shippingDateIso
    ? addWorkingDays(new Date(shippingDateIso), 5)
    : null;
  const remainingDays = maxDeliveryDate
    ? Math.max(0, calendarDaysUntil(maxDeliveryDate))
    : null;

  const shippingType = userData?.user?.shipping_type as string | undefined;
  const isGroupedShipping = shippingType === "grouped";
  const groupDeliveryAddress =
    userData?.group?.delivery_address ??
    pickGroupDeliveryAddress(userData?.group?.delivery_addresses);
  const deliveryAddress = isGroupedShipping
    ? groupDeliveryAddress
    : userData?.user?.delivery_address;
  const recipientName = getRecipientField(deliveryAddress, "name");
  const recipientLine1 = getRecipientField(deliveryAddress, "line_1");
  const recipientLine2 = getRecipientField(deliveryAddress, "line_2");
  const recipientCity = getRecipientField(deliveryAddress, "city");
  const recipientPostalCode = getRecipientField(deliveryAddress, "postal_code");
  const recipientProvince = getRecipientField(deliveryAddress, "province");
  const recipientCountry = getRecipientField(deliveryAddress, "country");
  const recipientPhone = getRecipientField(deliveryAddress, "phone");
  const recipientEmail = getRecipientField(deliveryAddress, "email");
  const hasDeliveryAddress = Boolean(
    recipientName || recipientLine1 || recipientCity || recipientPostalCode,
  );
  // Individual delivery only: users cannot add/edit the shared group address here.
  const canEditAddress =
    !isGroupedShipping && userData?.user?.pending_to_ship === false;
  const isHybrid = userData?.group?.delivery_option === "hybrid";

  const handleShowSummaryModal = async () => {
    setIsLoadingSummary(true);
    try {
      const currentChoice = (userData?.user?.shipping_type ?? 'individual') as 'individual' | 'grouped';
      setDeliveryChoice(currentChoice);

      const customizationsForApi = (userData?.user?.customizations ?? [])
        .filter((c: any) => c.value && c.value !== 'false' && c.value.trim() !== '')
        .map((c: any) => ({ option_id: c.option_id, value: c.value }));

      const result = await productService.calculatePrice(userData?.group?.id, {
        product_id: userData?.user?.product_id,
        customizations: customizationsForApi,
        previous_order_id: userData?.user?.id,
        delivery_choice: currentChoice,
      });

      if (!result) {
        toast.error('No se pudo obtener el precio. Inténtalo de nuevo.');
        return;
      }

      setPriceResult(result);
      setDeliveryPrice(Number(result.breakdown.delivery_price) || 0);
      setShowSummaryModal(true);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false);
  };

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: "url(/background.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8 space-y-8">
        {/* ORDER SUMMARY */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 space-y-4">
          <div>
            <span className="text-sm text-gray-500">Bienvenido de nuevo</span>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-700">
              {userData?.user?.name?.split(" ")[0]}, aquí tienes el resumen de
              tu pedido.
            </h1>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Card
              title="Entrega máxima"
              value={
                isPendingDelivery
                  ? "Pendiente"
                  : formatDeliveryWindow(shippingDateIso)
              }
              icon={<CalendarClock className="w-4 h-4 text-red-500" />}
            />
            <Card
              title="Días restantes"
              value={isPendingDelivery ? "Pendiente" : String(remainingDays)}
              icon={<Clock className="w-4 h-4 text-red-500" />}
              progressLabel={isPendingDelivery ? "" : "días"}
              valueClassName={isPendingDelivery ? "" : "text-2xl"}
            />
            <Card
              title="Estado actual"
              value={currentStatusLabel}
              icon={<TrendingUp className="w-4 h-4 text-red-500" />}
            />
            <Card
              title="Pagos grupales"
              icon={<CalendarClock className="w-4 h-4 text-red-500" />}
              value={userData?.group?.units_sold ?? 0}
              valueClassName="text-2xl"
              progressDetail="pagos realizados"
            />
          </div>
        </div>

        {/* ORDER DETAILS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 group hover:shadow-md transition-all duration-300 hover:border-red-200 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:border-gray-100"
            onClick={() => navigate(`/customize?code=${userData?.group?.code}`)}
            disabled={userData?.group?.status_id !== 2}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-4">
                <div className="bg-red-50 rounded-lg p-3 group-hover:bg-red-100 transition-colors duration-300">
                  <Pencil className="w-5 h-5 text-red-500" />
                </div>
                {userData?.group?.status_id !== 2 ? (
                  <div>
                    <p className="text-lg font-medium text-gray-800 text-left">
                      No es posible realizar cambios.
                    </p>
                    <span className="text-xs text-slate-400 font-semibold">
                      Una vez el pedido ha sido confirmado por el organizador.
                    </span>
                  </div>
                ) : (
                  <div>
                    <p className="text-lg font-medium text-gray-800 text-left">
                      Cambiar mi pedido
                    </p>
                    <span className="text-xs text-slate-400 font-semibold">
                      Modificar tamaños, colores o personalizaciones
                    </span>
                  </div>
                )}
              </div>

              <div className="">
                <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-red-500 transition-colors duration-300" />
              </div>
            </div>
          </button>

          <div
            className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 group hover:shadow-md transition-all duration-300 hover:border-red-200 cursor-pointer"
            onClick={() => setIsOrderDetailsOpen(true)}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-4">
                <div className="bg-red-50 rounded-lg p-3 group-hover:bg-red-100 transition-colors duration-300">
                  <Eye className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">
                    Ver mi pedido actual
                  </p>
                  <span className="text-xs text-slate-400 font-semibold">
                    Ver los detalles de tu pedido actual de un vistazo
                  </span>
                </div>
              </div>

              <div className="">
                <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-red-500 transition-colors duration-300" />
              </div>
            </div>
          </div>
        </div>

        {/* GROUP STATUS */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-700">
              Estado de grupo
            </h2>
            <div className="">
              Paso {currentStepNumber} de {GROUP_STATUS_STEPS.length}
            </div>
          </div>
          <div className="mt-6">
            <GroupStatusIndicator steps={timelineSteps} />
          </div>
        </div>

        {/* GROUP FILES */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-700">
            Diseño de grupo
          </h2>

          <div className="flex gap-4 space-y-1">
            <div className="bg-red-50 rounded-lg p-2 group-hover:bg-red-100 transition-colors duration-300 h-fit mt-2">
              <FileSpreadsheet className="w-5 h-5 text-red-500" />
            </div>
            <div className="space-y-2">
              {finalArtsUrl && userData?.group?.status_id >= 3 ? (
                <>
                  <div className="flex items-center gap-2">
                    <CircleCheck className="w-4 h-4 text-green-500" />
                    <span className="text-slate-800">
                      El diseño de grupo está listo.
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setPreview({
                          url: finalArtsUrl,
                          title: "Group Design",
                          filename: filenameFromUrl(
                            finalArtsUrl,
                            "group-design.jpg",
                          ),
                        })
                      }
                      className="px-3 py-1.5 rounded-lg text-sm transition-all relative flex items-center gap-2 shadow-sm hover:shadow-md border border-gray-200 hover:bg-gray-50 text-gray-700"
                    >
                      <Eye className="w-4 h-4 text-gray-500" /> Avance
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        downloadFile(
                          finalArtsUrl,
                          filenameFromUrl(finalArtsUrl, "group-design.jpg"),
                        )
                      }
                      className="px-3 py-1.5 rounded-lg text-sm transition-all relative flex items-center gap-2 shadow-sm hover:shadow-md bg-red-50 hover:bg-red-100 text-red-500 font-semibold"
                    >
                      <Download className="w-4 h-4 text-red-500" /> Descargar
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-500" />
                  <span className="text-slate-800">
                    El diseño de grupos aún no está disponible.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SHIPPING STATUS */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-700">
                Estado del envío
              </h2>
              <span className="text-sm text-gray-500">
                Realiza el seguimiento de tu pedido desde la producción hasta la
                entrega.
              </span>
            </div>
            <div className="bg-red-50 rounded-full px-2 py-1 h-fit flex items-center gap-2">
              <ShippingBadgeIcon className="w-4 h-4 text-red-500" />
              <span className="text-xs font-semibold text-red-500">
                {currentShippingStep?.label ?? "Pending"}
              </span>
            </div>
          </div>
          <div className="mt-6">
            <ShipmentStatusIndicator
              orderConfirmed={orderConfirmed}
              inProduction={inProduction}
              shippingStatus={shippingStatus}
            />
          </div>
        </div>
              
        {/* DELIVERY ADDRESS */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-700">
            Dirección de entrega
          </h2>
          <div className="flex items-center justify-between gap-2">
            <div className="mt-2 flex items-start gap-3">
              <div className="bg-red-50 rounded-lg p-2 h-fit">
                <MapPin className="w-5 h-5 text-red-500" />
              </div>
              <div className="space-y-1">
                {isGroupedShipping && (
                  <p className="text-sm font-medium text-gray-800">
                    Tu pedido se entregará en la dirección del grupo.
                  </p>
                )}
                {hasDeliveryAddress ? (
                  <div className="text-sm text-gray-700 flex gap-3 flex-wrap">
                    <div className="">
                      {recipientLine1 && (
                        <p>
                          <span className="text-gray-500">Dirección:</span>{" "}
                          {recipientLine1}
                        </p>
                      )}
                      {recipientLine2 && (
                        <p>
                          <span className="text-gray-500">Complemento:</span>{" "}
                          {recipientLine2}
                        </p>
                      )}
                      {recipientPostalCode && (
                        <p>
                          <span className="text-gray-500">Código postal:</span>{" "}
                          {recipientPostalCode}
                        </p>
                      )}
                      {recipientCity && (
                        <p>
                          <span className="text-gray-500">Ciudad:</span>{" "}
                          {recipientCity}
                        </p>
                      )}
                      {recipientProvince && (
                        <p>
                          <span className="text-gray-500">Provincia:</span>{" "}
                          {recipientProvince}
                        </p>
                      )}
                      {recipientCountry && (
                        <p>
                          <span className="text-gray-500">País:</span>{" "}
                          {recipientCountry}
                        </p>
                      )}
                    </div>

                    <div className="">
                      {recipientName && (
                        <p>
                          <span className="text-gray-500">Nombre:</span>{" "}
                          <span className="font-medium text-gray-800">
                            {recipientName}
                          </span>
                        </p>
                      )}

                      {recipientPhone && (
                        <p>
                          <span className="text-gray-500">Teléfono:</span>{" "}
                          {recipientPhone}
                        </p>
                      )}
                      {recipientEmail && (
                        <p>
                          <span className="text-gray-500">Email:</span>{" "}
                          {recipientEmail}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    {isGroupedShipping
                      ? "La dirección del grupo aún no está disponible."
                      : "La dirección de entrega aún no está disponible."}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 items-end">
            {/* ACTIONS — only for individual delivery */}
            {canEditAddress && (
              <div className="">
                {hasDeliveryAddress ? (
                  <button
                    type="button"
                    onClick={() => setIsChangeAddressOpen(true)}
                    className="px-3 py-1.5 rounded-lg text-sm transition-all relative flex items-center gap-2 shadow-sm hover:shadow-md border border-gray-200 hover:bg-gray-50 text-gray-700"
                  >
                    <Pencil className="w-4 h-4 text-gray-500" /> Editar dirección
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsChangeAddressOpen(true)}
                    className="px-3 py-1.5 rounded-lg text-sm transition-all relative flex items-center gap-2 shadow-sm hover:shadow-md bg-red-500 hover:bg-red-600 text-white"
                  >
                    <MapPin className="w-4 h-4 text-white" /> Añadir dirección
                  </button>
                )}
              </div>
            )}

            {isHybrid && (
              <div className="">
                <button
                  type="button"
                  onClick={handleShowSummaryModal}
                  disabled={isLoadingSummary}
                  className="px-3 py-1.5 rounded-lg text-sm transition-all relative flex items-center gap-2 shadow-sm hover:shadow-md border border-gray-200 hover:bg-gray-50 text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoadingSummary ? (
                    <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <Truck className="w-4 h-4 text-gray-500" />
                  )}
                  Cambiar la opción
                </button>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <OrderDetailsModal
        isOpen={isOrderDetailsOpen}
        onClose={() => setIsOrderDetailsOpen(false)}
        product={product}
        customizations={customizations}
      />
      <PreviewModal
        url={preview?.url ?? null}
        title={preview?.title}
        kind={preview?.kind}
        filename={preview?.filename}
        onClose={() => setPreview(null)}
      />
      <ChangeDeliveryAddressModal
        isOpen={isChangeAddressOpen}
        onClose={() => setIsChangeAddressOpen(false)}
        groupOrderId={userData?.user?.id ?? ""}
        initialAddress={deliveryAddress}
        onSuccess={(address) => {
          setUserData((prev: any) => {
            if (!prev?.user) return prev;
            const next = {
              ...prev,
              user: {
                ...prev.user,
                delivery_address: address,
              },
            };
            localStorage.setItem("userData", JSON.stringify(next));
            return next;
          });
        }}
      />
      {showSummaryModal && priceResult && (
        <OrderSummaryModal
          isOpen={showSummaryModal}
          onClose={handleCloseSummaryModal}
          order={{
            id: userData?.user?.id,
            group_id: userData?.group?.id ?? '',
            name: userData?.user?.name ?? '',
            email: userData?.user?.email ?? '',
            phone: userData?.user?.phone ?? '',
            product_id: userData?.user?.product_id,
            garment_id: userData?.user?.product?.garment_id,
            garment_name: userData?.user?.product?.garment?.name,
            color_id: userData?.user?.product?.color_id,
            color_name: userData?.user?.product?.color?.name,
            color_hex_code: userData?.user?.product?.color?.hex_code,
            size_id: userData?.user?.product?.size_id,
            size_name: userData?.user?.product?.size?.name,
            customizations: (userData?.user?.customizations ?? []) as any,
            total_price: priceResult.total_price,
            final_price: priceResult.breakdown.total,
            is_change: true,
            order_change: priceResult.order_change,
          }}
          groupId={userData?.group?.id ?? ''}
          deliveryOption={userData?.group?.delivery_option ?? 'hybrid'}
          deliveryChoice={deliveryChoice}
          onDeliveryChoiceChange={setDeliveryChoice}
          deliveryPrice={deliveryPrice}
          individualOptionPrice={Number(userData?.group?.individual_option_price) || 0}
          groupedOptionPrice={Number(userData?.group?.grouped_option_price) || 0}
          groupDeliveryAddress={pickGroupDeliveryAddress(userData?.group?.delivery_addresses) ?? undefined}
          userDeliveryAddress={userData?.user?.delivery_address ?? undefined}
          isAddressChangeOnly={true}
        />
      )}
    </div>
  );
}
